import React, { useState } from 'react';
import { CircularProgress, Tab, Tabs, styled } from '@mui/material';
import { RetryButton } from 'components/RetryButton/RetryButton';
import { useInfiniteScroll } from 'hooks/useInfiniteScroll';
import {
  RESOURCE_SECTION_CARD_GUTTER,
  RESOURCE_SECTION_MAX_WIDTH,
  RESOURCE_SECTION_PADDING,
} from 'themes/constants';
import { Navigate, useNavigate } from 'react-router';
import { ContainerLayout } from 'components/index';
import { LibraryCard } from 'components/LibraryCard/LibraryCard';
import { ArticleView } from 'components/ArticleView/ArticleView';
import { Alert } from 'components/Alert/Alert';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import {
  useResources,
  useLibraryArticles,
  useLibraryArticleFavorites,
} from 'features/resources/hooks';
import { SearchHeaderBar } from 'components/SearchHeaderBar/SearchHeaderBar';
import { toLibraryArticleFavoriteRequest } from 'features/resources/utils/libraryArticleFavoriteUtils';
import { TransformedLibraryArticle } from 'features/resources/types';
import { EmptyState } from 'components/EmptyState/EmptyState';
import NoFavoritesTwo from 'assets/images/EmptyState/NoFavorites_two.svg';
import { usePendo } from 'features/pendo/hooks/usePendo';
import { PendoEventType } from 'features/pendo/types';
import { ResourceCardGrid, ResourceCardGridCell } from './ResourceCardGrid';

const InfiniteScrollContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2, 0),
}));

const PageColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0,
}));

const ScrollArea = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.brandBackgroundSecondary.main}`,
}));

// Mobile-only page: same width cap as the mobile resource sections (2 cards per row) at every breakpoint.
const CardsFrame = styled('div')(({ theme }) => ({
  // maxWidth includes the padding (RESOURCE_SECTION_MAX_WIDTH); no global CssBaseline sets this.
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: RESOURCE_SECTION_CARD_GUTTER,
  maxWidth: RESOURCE_SECTION_MAX_WIDTH.xs,
  margin: '0 auto',
  padding: theme.spacing(RESOURCE_SECTION_PADDING.xs),
  paddingBottom: theme.spacing(18),
}));
type LibraryTab = 'all' | 'favorites';

export const LibraryPage = () => {
  const navigate = useNavigate();
  const { translation } = useTranslationContext();
  const { isMobile } = useDeviceType();
  const {
    libraryArticles,
    hasMoreLibraryArticles,
    isFetchingLibraryArticles,
    loadMoreLibraryArticles,
    isLoadMoreError,
    refetchLibraryArticles,
  } = useLibraryArticles();
  const { handleArticleClick, activeArticle, handleModalClose } = useResources();
  const sentinelRef = useInfiniteScroll(loadMoreLibraryArticles);
  const { favoritedArticles, isFavorited, toggleFavorite } =
    useLibraryArticleFavorites(libraryArticles);
  const [tab, setTab] = useState<LibraryTab>('all');
  const [isFavoriteToastPresented, setIsFavoriteToastPresented] = useState(false);
  const { trackEvent } = usePendo();
  const goBack = () => navigate(-1);

  const handleToggleFavorite = async (article: TransformedLibraryArticle) => {
    try {
      const result = await toggleFavorite(toLibraryArticleFavoriteRequest(article));
      if (result === 'added') {
        trackEvent(PendoEventType.FAVORITE_ARTICLE, {
          eventType: PendoEventType.FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
        setIsFavoriteToastPresented(true);
      } else {
        trackEvent(PendoEventType.REMOVED_FAVORITE_ARTICLE, {
          eventType: PendoEventType.REMOVED_FAVORITE_ARTICLE,
          articleId: `${article.id}`,
        });
      }
    } catch (error) {
      // Favorite failed; no toast shown.
      // throwing error to be catched and rollback the local state in LibraryCard
      throw new Error((error as Error)?.message || 'Failed to toggle favorite');
    }
  };

  const visibleArticles = tab === 'favorites' ? favoritedArticles : libraryArticles;

  // Favorites come from the favorites API (all of them), so only the All tab paginates.
  const canLoadMore = tab === 'all' && hasMoreLibraryArticles;

  // Relative redirect back to the Resources index, staying within the cobrand base.
  if (!isMobile) {
    return <Navigate to=".." replace />;
  }

  return (
    <ContainerLayout>
      <PageColumn>
        <SearchHeaderBar
          title={translation('resources_library_header')}
          onClickBack={goBack}
          showSearch
          searchAriaLabel={translation('search_bar_copy')}
          onClickSearch={() => navigate('search')}
          disableElevation
          headerStyles={{
            borderTop: (theme) => `1px solid ${theme.palette.brandBackgroundPrimary.main}`,
            boxShadow: (theme) => `0 -1px 1px 0 ${theme.palette.blackWithOpacity40}`,
          }}
        />
        <StyledTabs
          value={tab}
          onChange={(_, value: LibraryTab) => setTab(value)}
          variant="fullWidth"
          aria-label={translation('resources_library_header')}
        >
          <Tab label={translation('all_articles')} value="all" />
          <Tab label={translation('my_favorites')} value="favorites" />
        </StyledTabs>
        {tab === 'favorites' && visibleArticles.length === 0 ? (
          <EmptyState
            title={translation('favorites_empty_state_title')}
            subtitle={translation('favorites_empty_state_body')}
            imageArray={[NoFavoritesTwo]}
            style={{ height: '60%', padding: 16 }}
          />
        ) : (
          <ScrollArea>
            <CardsFrame>
              <ResourceCardGrid>
                {visibleArticles.map((article) => (
                  <ResourceCardGridCell key={article.id}>
                    <LibraryCard
                      title={article.title}
                      imageUrl={article.imageUrl}
                      onClick={() => handleArticleClick(article)}
                      ariaLabel={`${article.title} article`}
                      showFavorite
                      isFavorited={isFavorited(article.contentUrl)}
                      onToggleFavorite={() => handleToggleFavorite(article)}
                    />
                  </ResourceCardGridCell>
                ))}
              </ResourceCardGrid>
              {canLoadMore && !isLoadMoreError && (
                <InfiniteScrollContainer ref={sentinelRef}>
                  {isFetchingLibraryArticles && <CircularProgress size={28} />}
                </InfiniteScrollContainer>
              )}
              {tab === 'all' && isLoadMoreError && (
                <InfiniteScrollContainer>
                  <RetryButton onClick={() => refetchLibraryArticles()} />
                </InfiniteScrollContainer>
              )}
            </CardsFrame>
          </ScrollArea>
        )}
      </PageColumn>

      {activeArticle && (
        <ArticleView
          showHeader
          showFavorite
          title={activeArticle.title}
          open
          url={activeArticle.contentUrl}
          isFavorited={isFavorited(activeArticle.contentUrl)}
          onToggleFavorite={() => handleToggleFavorite(activeArticle)}
          onClose={handleModalClose}
          overrideBackButtonVisible
          onClickBack={handleModalClose}
        />
      )}

      <Alert
        isPresented={isFavoriteToastPresented}
        handleDismiss={() => setIsFavoriteToastPresented(false)}
        message={translation('content_favorites_add')}
        presentationDuration={5000}
        variant="filled"
        icon={false}
        snackbarSx={{ marginBottom: 3.5 }}
        sx={{ backgroundColor: 'brandShade2.main' }}
      />
    </ContainerLayout>
  );
};
